import React, { memo } from 'react';
import { useVFXConfig } from '@/features/vfx/hooks/useVFXConfig';
import BackgroundLayer from '@/features/vfx/components/BackgroundLayer';
import VFXCanvas from '@/features/vfx/components/VFXCanvas';
import { ToastHost } from '@/components/ui';
import AutoLogoutWarning from '@/components/AutoLogoutWarning';
import { useAutoRedirect } from '@/hooks/useAutoRedirect';
import { useKioskHeartbeat } from '@/hooks/useKioskHeartbeat';
import { useKioskKeyboardAssist } from '@/hooks/useKioskKeyboardAssist';
import { usePageAnimationsEnabled } from '@/contexts/AnimationContext';
import { useStyleHasArt } from '@/hooks/useStyleHasArt';
import KioskOfflineOverlay from '@/components/KioskOfflineOverlay';
import RedeemApprovalGate from '@/features/redeemApproval/RedeemApprovalGate';

interface BaseLayoutProps {
  children: React.ReactNode;
}

// Stable sub-components so VFX/background updates don't re-render page content
const GlobalOverlays = memo(() => (
  <>
    <ToastHost />
    <AutoLogoutWarning />
    <KioskOfflineOverlay />
    {/* Shop / Cashier redeem waiting for this Player's approval — every page */}
    <RedeemApprovalGate />
  </>
));
GlobalOverlays.displayName = 'GlobalOverlays';

const BaseLayout: React.FC<BaseLayoutProps> = ({ children }) => {
  const { vfxConfig, backgroundConfig } = useVFXConfig();
  /** Disable the particle canvas when the user turns animations off (CSS handles the rest) */
  const pageAnimationsEnabled = usePageAnimationsEnabled();
  /** Particles are background art: only styles with art draw them (BackgroundLayer gates the rest). */
  const styleHasArt = useStyleHasArt();

  useAutoRedirect();
  useKioskHeartbeat();
  // Kiosks: the focused field stays above the on-screen keyboard (no-op elsewhere).
  useKioskKeyboardAssist();

  return (
    <div className="relative flex flex-col h-dvh min-h-0 overflow-hidden">
      <BackgroundLayer config={backgroundConfig} />
      {styleHasArt && pageAnimationsEnabled && <VFXCanvas config={vfxConfig} />}
      <div className="relative z-1 flex flex-1 min-h-0 flex-col overflow-hidden">
        {children}
      </div>
      <GlobalOverlays />
    </div>
  );
};

export default memo(BaseLayout);
